import { useEffect } from 'react';
import api from '../../utils/api';
import { isEmbed, postToParent, isAllowedPortalOrigin } from '../../utils/embed';

// Bridges the embedded app to the parent portal via postMessage.
//   app  → portal:  ma:ready, ma:resize {height}, ma:needs-token (from api.js), ma:signout
//   portal → app:   ma:token {token}  (in response to ma:needs-token)
// Mounted once, near the app root. Does nothing unless running in embed mode.
export default function EmbedBridge() {
  useEffect(() => {
    if (!isEmbed()) return;

    postToParent({ type: 'ma:ready' });

    // Report content height so the portal can size the iframe (cross-origin, so
    // the portal cannot read our height directly — we push it).
    let lastHeight = 0;
    const postHeight = () => {
      const h = Math.ceil(document.documentElement.scrollHeight);
      if (h && h !== lastHeight) {
        lastHeight = h;
        postToParent({ type: 'ma:resize', height: h });
      }
    };
    postHeight();

    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(postHeight);
      ro.observe(document.body);
    }
    window.addEventListener('resize', postHeight);
    const interval = setInterval(postHeight, 1000); // safety net for late layout shifts

    // Receive a fresh token from the portal and re-establish the session.
    const onMessage = (event) => {
      if (!isAllowedPortalOrigin(event.origin)) return; // origin allowlist
      const data = event.data;
      if (!data || typeof data !== 'object') return;
      if (data.type === 'ma:token' && typeof data.token === 'string' && data.token) {
        window.location.assign('/sso?token=' + encodeURIComponent(data.token));
      } else if (data.type === 'ma:signout') {
        // Portal is logging out — end the app session too (logout sync, plan §13-Q11).
        // /auth/ is excluded from the 401 re-handshake, so a dead session won't loop.
        try { window.sessionStorage.removeItem('ma_embed'); } catch { /* ignore */ }
        api.post('/auth/logout').catch(() => {}).finally(() => {
          postToParent({ type: 'ma:signout' }); // ack back to the portal
        });
      }
    };
    window.addEventListener('message', onMessage);

    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', postHeight);
      window.removeEventListener('message', onMessage);
      clearInterval(interval);
    };
  }, []);

  return null;
}
